import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getFileType, isImageExtension, isFontExtension } from '@/lib/fileConstants';

export function useAgentFiles(agentId?: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['agent-files', agentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('agent_files')
        .select('*')
        .eq('agent_id', agentId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user && !!agentId,
    refetchInterval: (query) => {
      const files = query.state.data;
      if (!files) return false;
      const hasPending = files.some(f => f.parsing_status === 'pending' || f.parsing_status === 'processing');
      return hasPending ? 3000 : false;
    },
  });
}

export function useUploadAgentFile() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ agentId, file }: { agentId: string; file: File }) => {
      if (!user) throw new Error('Usuário não autenticado');

      const fileId = crypto.randomUUID();
      const sanitizedName = file.name
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `${agentId}/${fileId}/${sanitizedName}`;

      const { error: uploadError } = await supabase.storage
        .from('agent-files')
        .upload(storagePath, file);
      if (uploadError) throw uploadError;

      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      const isImage = isImageExtension(file.name);
      const isFont = isFontExtension(file.name);

      const { data, error: insertError } = await supabase
        .from('agent_files')
        .insert({
          id: fileId,
          agent_id: agentId,
          file_name: file.name,
          file_type: getFileType(file.name),
          file_size_bytes: file.size,
          mime_type: file.type || 'application/octet-stream',
          storage_path: storagePath,
          uploaded_by: user.id,
          parsing_status: isImage || isFont ? 'ready' : 'pending',
        })
        .select()
        .single();
      if (insertError) throw insertError;

      // For documents, trigger parsing. Images and fonts are binary assets.
      if (!isImage && !isFont) {
        supabase.functions.invoke('parse-document', {
          body: {
            chatFileId: fileId,
            storageBucket: 'agent-files',
            storagePath,
            fileType: getFileType(file.name),
          },
        }).then(async ({ data: parseResult, error }) => {
          if (error) {
            console.error('parse-document invoke error:', error);
            await supabase.from('agent_files').update({ parsing_status: 'error', parsing_error: error.message }).eq('id', fileId);
          } else if (parseResult) {
            await supabase.from('agent_files').update({
              parsing_status: 'ready',
              content_text: parseResult.content_text,
            }).eq('id', fileId);
          }
          queryClient.invalidateQueries({ queryKey: ['agent-files', agentId] });
        });
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agent-files', data.agent_id] });
    },
  });
}

export function useDeleteAgentFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ fileId, storagePath, agentId }: { fileId: string; storagePath: string; agentId: string }) => {
      const { error: storageError } = await supabase.storage
        .from('agent-files')
        .remove([storagePath]);
      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from('agent_files')
        .delete()
        .eq('id', fileId)
        .select('id')
        .single();
      if (dbError) throw dbError;

      return { agentId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agent-files', data.agentId] });
    },
  });
}
